import { useDispatchQueueView } from '@/hooks/scheduler/useDispatchQueueView';
import { DispatchStageQueues } from '@/components/dispatch/DispatchStageQueues';
import { DispatchColorLegend } from '@/components/dispatch/DispatchColorLegend';
import { useDispatchQueueSync } from '@/hooks/scheduler/useDispatchQueueSync';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { assertOfficeScheduleAccess } from '@/lib/auth/officeScheduleGuard';
import {
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  CloudRain,
  Loader2,
  Map as MapIcon,
  RadioTower,
  Rows3,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import { addDays, format, isValid, parseISO, subDays } from 'date-fns';
import { Button } from '@/components/atoms/button';
import { Badge } from '@/components/atoms/badge';
import { Calendar } from '@/components/atoms/calendar';
import { Sheet, SheetContent } from '@/components/atoms/sheet';
import { Tabs, TabsList, TabsTrigger } from '@/components/atoms/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/popover';
import { DispatchChoiceMenu } from '@/components/dispatch/DispatchChoiceMenu';
import { cn } from '@/lib/utils';
import {
  formatDateTimeInTimeZone,
  formatTimeZoneLabel,
  timeZoneAbbrev,
  getViewerTimeZone,
} from '@/lib/date-values';
import { ScheduleWorkspaceShell } from '@/components/layouts';
import { ScheduleTimezoneToggle } from '@/components/molecules/ScheduleTimezoneToggle';
import { DispatchBoard } from '@/components/dispatch/DispatchBoard';
import { DispatchWeekBoard } from '@/components/dispatch/DispatchWeekBoard';
import {
  NO_TEAM,
  bookingHasTechnician,
  dispatchTeamOptions,
  dispatchWeekDates,
  visibleLegendBookings,
} from '@/components/dispatch/dispatch-week';
import { DispatchMapPanel } from '@/components/dispatch/DispatchMapPanel';
import { RoutesMapView } from '@/components/dispatch/RoutesMapView';
import { RoutesOverview } from '@/components/dispatch/RoutesOverview';
import { COLOR_BY_VALUES, DEFAULT_COLOR_BY, type ColorBy } from '@/components/dispatch/tech-colors';
import {
  loadFilterPresets,
  saveFilterPreset,
  deleteFilterPreset,
  type DispatchFilterPreset,
} from '@/components/dispatch/filter-presets';
import { useTenantId } from '@/stores/tenant.store';
import { tenantScopedKey } from '@/lib/tenantStorage';
import { BookingDetailSheet } from '@/components/calendar/BookingDetailSheet';
import { TeamsManagementPanel } from '@/components/organisms/teams/TeamsManagementPanel';
import { WeatherHoldFlowPanel } from '@/components/dispatch/WeatherHoldFlowPanel';

import { useHasPermission } from '@/hooks/usePermissions';
import {
  useAllSchedulerBookings,
  useTeamSchedulerBookings,
  useSchedulerBookings,
} from '@/hooks/scheduler/useSchedulerBookings';
import { useSchedulerTechnicians } from '@/hooks/scheduler/useSchedulerTechnicians';
import { useTerritories } from '@/hooks/territories/useTerritories';
import { useJobTypes } from '@/hooks/settings/useJobTypes';
import { useBusinessUnits } from '@/hooks/scheduler/useBusinessUnits';
import { useBusinessSettings } from '@/hooks/settings/useBusinessSettings';
import {
  useRouteSummaries,
  type RouteSummaryFilters,
} from '@/hooks/scheduler/useRouteOptimization';
import { useScheduleTimezone } from '@/lib/time/scheduleTimezone';
import type {
  AppointmentStatus,
  BookingListParams,
  JobPriority,
  SchedulerBooking,
} from '@/types/scheduler';

type MapMode = 'on' | 'off';
type DispatchView = 'board' | 'routes';
type BoardPeriod = 'day' | 'week';
type RoutesTab = 'overview' | 'map';
const PRIORITY_VALUES: JobPriority[] = ['EMERGENCY', 'HIGH', 'NORMAL', 'LOW'];
// NOT-542 — Business Unit facet "Unassigned" option. Matches the operations
// `businessUnitId=unassigned` sentinel (→ appointments with no BU).
const UNASSIGNED_BUSINESS_UNIT = 'unassigned';
const ROUTE_MAP_EXCLUDED_STATUSES = new Set<AppointmentStatus>([
  'CANCELLATION_PENDING',
  'CANCELLED',
  'SKIPPED',
  'EXPIRED',
]);

function toDateString(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function parseLocalDate(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}
/** "Today" (YYYY-MM-DD) in the given tz — the board renders in business time, so
 *  the default day + the Today button must follow it, not the browser clock. */
function todayInTz(tz: string | null): string {
  if (!tz) return toDateString(new Date());
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

function isRouteMapBooking(booking: SchedulerBooking): boolean {
  return !ROUTE_MAP_EXCLUDED_STATUSES.has(booking.status);
}

export const Route = createFileRoute('/_dashboard/dashboard/dispatch')({
  beforeLoad: () => assertOfficeScheduleAccess(),
  validateSearch: (search: Record<string, unknown>) => ({
    // No browser-clock default — the component fills it from the business tz so a
    // dispatcher in another zone never lands on the wrong day near midnight.
    date:
      typeof search.date === 'string' &&
      /^\d{4}-\d{2}-\d{2}$/.test(search.date) &&
      isValid(parseISO(search.date))
        ? search.date
        : undefined,
    // NOT-329 — `jobtype` is the default, but resolving it here would hard-code a
    // mode into the search state for every visit. The §5.9 restore below only
    // reads localStorage when the URL has no `colorBy`, and callers must be able
    // to leave it unset, so an absent/invalid param stays undefined and
    // DEFAULT_COLOR_BY is applied at the point of use. The whitelist has to list
    // `jobtype` too, or an explicit ?colorBy=jobtype survives by luck.
    colorBy: COLOR_BY_VALUES.includes(search.colorBy as ColorBy)
      ? (search.colorBy as ColorBy)
      : undefined,
    // Map is a companion panel, off by default — the calm view is just the schedule.
    map: (search.map === 'on' ? 'on' : 'off') as MapMode,
    ...(search.view === 'routes' || search.view === 'board'
      ? { view: search.view as DispatchView }
      : {}),
    ...(search.routesTab === 'map' || search.routesTab === 'overview'
      ? { routesTab: search.routesTab as RoutesTab }
      : {}),
    ...(search.period === 'week' || search.period === 'day'
      ? { period: search.period as BoardPeriod }
      : {}),
    ...(typeof search.teamId === 'string' ? { teamId: search.teamId } : {}),
    ...(search.queue === 'only' ? { queue: 'only' as const } : {}),
    ...(typeof search.pipelineId === 'string' ? { pipelineId: search.pipelineId } : {}),
    bookingId: typeof search.bookingId === 'string' ? search.bookingId : undefined,
    // §5.8 faceted filters — shareable / persisted in the URL.
    territoryId: typeof search.territoryId === 'string' ? search.territoryId : undefined,
    jobTypeId: typeof search.jobTypeId === 'string' ? search.jobTypeId : undefined,
    // NOT-542 — Business Unit facet (a BU id, or the 'unassigned' sentinel).
    businessUnitId: typeof search.businessUnitId === 'string' ? search.businessUnitId : undefined,
    priority: PRIORITY_VALUES.includes(search.priority as JobPriority)
      ? (search.priority as JobPriority)
      : undefined,
    // §5.8/§21.2 — the AI-vs-manual facet is deferred until AI auto-dispatch
    // exists (the `source startsWith 'ai'` signal isn't written by any real
    // path — it only ever matched the schema default). Force it off so a stale
    // URL or saved preset can't resurrect a filter that lies. Backend `origin`
    // param + repo wiring stay (harmless) for the future AI-dispatch epic.
    origin: undefined as 'ai' | 'manual' | undefined,
    exceptions: search.exceptions === 'true' ? true : undefined,
  }),
  component: DispatchPage,
});

/** Dispatch — the operational cockpit (run today). Calm by default: just the
 *  tech-row schedule on business hours. The "Needs a tech" queue, the map, and
 *  the filters come in on demand from a quiet chip row. Default Schedule
 *  surface, with a weekly team view for planning across days. */
function DispatchPage() {
  useDispatchQueueSync();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const {
    data: businessSettings,
    isError: settingsError,
    refetch: refetchSettings,
  } = useBusinessSettings();
  const businessTz = businessSettings?.timezone ?? null;
  const scheduleDisplay = useScheduleTimezone();
  const displayTz = scheduleDisplay.displayTimezone;

  // Default to "today" in the business tz (the zone the board renders in), not the
  // browser clock. While settings load there's no business tz yet, so the implicit
  // date isn't trustworthy — `dateReady` gates date nav until it is, and the effect
  // below pins the URL to the business day so the nav never operates on the wrong
  // base (a cross-tz dispatcher near midnight would otherwise persist the wrong day).
  // The whole board (event placement, now-line) renders in the business tz, which
  // comes from /settings. Until that resolves, DispatchBoard falls back to the
  // viewer's local zone and currentDate falls back to the browser day — both wrong
  // for a cross-tz tenant. getEffectiveSettings always returns a timezone on
  // success, so `businessTz === null` means the query is still loading OR errored.
  // Gate EVERYTHING (board + date controls) on a resolved businessTz — an explicit
  // ?date is not enough, because the board still needs the tz to paint correctly.
  const hasExplicitDate = Boolean(search.date);
  const dateReady = businessTz !== null;
  const currentDate = search.date ?? todayInTz(businessTz);
  const colorBy = search.colorBy ?? DEFAULT_COLOR_BY;
  const mapMode = search.map;
  const dispatchView = search.view ?? 'board';
  const routesTab = search.routesTab ?? 'overview';
  const period = search.period ?? 'day';
  const isWeek = dispatchView === 'board' && period === 'week';
  const dateObj = useMemo(() => parseLocalDate(currentDate), [currentDate]);
  const isToday = currentDate === todayInTz(businessTz);
  const weekDates = useMemo(() => dispatchWeekDates(currentDate), [currentDate]);
  const weekLabel = `${format(parseLocalDate(weekDates[0]!), 'MMM d')} – ${format(parseLocalDate(weekDates[6]!), 'MMM d, yyyy')}`;
  const isThisWeek = weekDates.includes(todayInTz(businessTz));

  // Pin the implicit date into the URL as the business-tz day (replace, no history
  // entry) only once the tz is known, so the nav never operates on the fallback.
  useEffect(() => {
    if (!hasExplicitDate && businessTz) {
      void navigate({
        replace: true,
        search: (prev) => ({ ...prev, date: todayInTz(businessTz) }),
      });
    }
  }, [hasExplicitDate, businessTz, navigate]);

  const canManageWeatherHold = useHasPermission('dispatch:reassign');
  const canManageTeams = useHasPermission('technicians:view');

  const [techFilter, setTechFilter] = useState<string>('all');
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [queueOpen, setQueueOpen] = useState(false);
  const [queueView, setQueueView] = useDispatchQueueView();
  const queueOnly = search.queue === 'only';
  const [selectedBooking, setSelectedBooking] = useState<SchedulerBooking | null>(null);
  const [selectedRouteTechnicianId, setSelectedRouteTechnicianId] = useState<string>();
  const [detailOpen, setDetailOpen] = useState(false);
  const [teamsOpen, setTeamsOpen] = useState(false);
  // Cross-highlight between the board tiles and the map companion (hover one,
  // the matching stop lights up in the other).
  const [hoveredBookingId, setHoveredBookingId] = useState<string | null>(null);
  const [weatherHoldOpen, setWeatherHoldOpen] = useState(false);
  const [weatherDrawActive, setWeatherDrawActive] = useState(false);
  const [weatherDrawRing, setWeatherDrawRing] = useState<google.maps.LatLngLiteral[] | null>(null);

  const { data: territories = [] } = useTerritories();
  const { data: jobTypes = [] } = useJobTypes();
  // NOT-542 — active Business Units power the board's BU facet. Zero-cost: the
  // dropdown only renders when the tenant has ≥1 active unit.
  const { data: businessUnits = [] } = useBusinessUnits('active');
  const hasBusinessUnits = businessUnits.length > 0;
  const effectiveBusinessUnitId = hasBusinessUnits ? search.businessUnitId : undefined;

  // §5.8 — the faceted filters threaded into the board + map booking queries.
  const boardFilters = useMemo<BookingListParams>(
    () => ({
      ...(search.territoryId ? { territoryId: search.territoryId } : {}),
      ...(search.jobTypeId ? { jobTypeId: search.jobTypeId } : {}),
      ...(effectiveBusinessUnitId ? { businessUnitId: effectiveBusinessUnitId } : {}),
      ...(search.priority ? { priority: search.priority } : {}),
      ...(search.exceptions ? { hasExceptions: true } : {}),
    }),
    [
      search.territoryId,
      search.jobTypeId,
      effectiveBusinessUnitId,
      search.priority,
      search.exceptions,
    ],
  );
  const facetFilterCount = Object.keys(boardFilters).length;

  // Filters popover shows a count badge when anything is off its default.
  const activeFilterCount =
    (techFilter !== 'all' ? 1 : 0) + (colorBy !== 'jobtype' ? 1 : 0) + facetFilterCount;

  const {
    data: technicians = [],
    isFetching: techsLoading,
    isError: techniciansError,
    refetch: refetchTechnicians,
  } = useSchedulerTechnicians();
  const activeTechnicians = useMemo(() => technicians.filter((t) => t.isActive), [technicians]);
  const teams = useMemo(() => dispatchTeamOptions(activeTechnicians), [activeTechnicians]);
  const selectedTeam = teams.find((team) => team.id === search.teamId);
  const teamTechnicians = useMemo(
    () => activeTechnicians.filter((tech) => (tech.teamId ?? NO_TEAM) === selectedTeam?.id),
    [activeTechnicians, selectedTeam?.id],
  );
  const filterTechnicians = isWeek ? teamTechnicians : activeTechnicians;
  useEffect(() => {
    // Browser back/forward and roster edits can change the team without using
    // the picker. Drop a technician filter that no longer belongs to its team.
    if (
      isWeek &&
      selectedTeam &&
      techFilter !== 'all' &&
      !teamTechnicians.some((tech) => tech.id === techFilter)
    ) {
      setTechFilter('all');
    }
  }, [isWeek, selectedTeam, teamTechnicians, techFilter]);
  const weekTechnicianIds = useMemo(
    () =>
      teamTechnicians
        .filter((tech) => techFilter === 'all' || tech.id === techFilter)
        .map((tech) => tech.id),
    [teamTechnicians, techFilter],
  );
  const weekParams = useMemo(
    () => ({ ...boardFilters, dateFrom: weekDates[0], dateTo: weekDates[6] }),
    [boardFilters, weekDates],
  );
  const {
    data: weekBookings,
    isLoading: weekLoading,
    isError: weekError,
    refetch: refetchWeek,
  } = useTeamSchedulerBookings(weekParams, weekTechnicianIds, {
    enabled: dateReady && isWeek && Boolean(selectedTeam),
  });
  const filteredWeekBookings = useMemo(() => {
    const ids = new Set(weekTechnicianIds);
    return (weekBookings?.data ?? []).filter((booking) => bookingHasTechnician(booking, ids));
  }, [weekBookings?.data, weekTechnicianIds]);

  // The backlog ignores the selected day/technician, but honors business facets.
  // Read every page so a large holding queue cannot silently lose pending visits.
  const queueQuery = useAllSchedulerBookings(
    { ...boardFilters, statusGroup: 'unscheduled' },
    { refetchInterval: 30_000 },
  );
  const unscheduledBookings = useMemo(() => queueQuery.data?.data ?? [], [queueQuery.data?.data]);

  // The day's bookings — same query key as DispatchBoard's, so react-query
  // dedupes the fetch. Feeds the map's pins.
  const { data: dayBookings } = useSchedulerBookings(
    {
      dateFrom: currentDate,
      dateTo: currentDate,
      limit: 100,
      ...boardFilters,
    },
    { enabled: dateReady && !isWeek },
  );
  // Pins = the day's stops + unassigned queue (both colored/hollowed by the
  // panel); only geocoded ones actually render.
  const mapBookings = useMemo(
    () => [...(isWeek ? filteredWeekBookings : (dayBookings?.data ?? [])), ...unscheduledBookings],
    [isWeek, filteredWeekBookings, dayBookings?.data, unscheduledBookings],
  );
  // When a single tech is filtered, the map mirrors the board: show only that
  // crew's stops so it reads as *that tech's route*, instead of keeping the whole
  // fleet on the map (the other crews as faint dimmed pins) and zooming out to
  // fit all of them — which left the selected route a tiny cluster in a sea of
  // scattered dots. techFilter === 'all' keeps the full-fleet overview untouched.
  const mapPanelBookings = useMemo(
    () =>
      techFilter === 'all' ? mapBookings : mapBookings.filter((b) => b.technicianId === techFilter),
    [mapBookings, techFilter],
  );
  // What the legend explains must be what the dispatcher is looking at, or it
  // names colours that are nowhere on screen. `mapBookings` is the wrong set for
  // that: it always carries the unscheduled queue, even when the queue panel is
  // closed, and it is not narrowed the way the board narrows.
  const legendBookings = useMemo(
    () =>
      visibleLegendBookings({
        scheduled: isWeek ? filteredWeekBookings : (dayBookings?.data ?? []),
        unscheduled: unscheduledBookings,
        techFilter,
        isWeek,
        queueOpen,
        queueOnly,
      }),
    [
      dayBookings?.data,
      filteredWeekBookings,
      isWeek,
      queueOnly,
      queueOpen,
      techFilter,
      unscheduledBookings,
    ],
  );

  const routeSummaryFilters = useMemo<RouteSummaryFilters>(
    () => ({
      ...(search.territoryId ? { territoryId: search.territoryId } : {}),
      ...(search.jobTypeId ? { jobTypeId: search.jobTypeId } : {}),
      // NOT-542 — thread the BU facet so the Routes overview (technicians, counts,
      // capacity) matches the BU-filtered route-map stops; without it the overview
      // stays fleet-wide and selecting an other-BU route yields an empty map.
      ...(effectiveBusinessUnitId ? { businessUnitId: effectiveBusinessUnitId } : {}),
      ...(search.priority ? { priority: search.priority } : {}),
      ...(search.exceptions ? { hasExceptions: true } : {}),
    }),
    [
      search.territoryId,
      search.jobTypeId,
      effectiveBusinessUnitId,
      search.priority,
      search.exceptions,
    ],
  );
  const { data: routeSummaries = [], isFetching: routeSummariesLoading } = useRouteSummaries(
    currentDate,
    routeSummaryFilters,
    dateReady && dispatchView === 'routes',
  );
  const activeRouteTechnicianId = useMemo(() => {
    if (
      selectedRouteTechnicianId &&
      routeSummaries.some((summary) => summary.technicianId === selectedRouteTechnicianId)
    ) {
      return selectedRouteTechnicianId;
    }
    return routeSummaries[0]?.technicianId;
  }, [routeSummaries, selectedRouteTechnicianId]);
  const routeMapBookingParams = useMemo<BookingListParams>(
    () => ({
      dateFrom: currentDate,
      dateTo: currentDate,
      ...boardFilters,
    }),
    [boardFilters, currentDate],
  );
  const { data: routeMapBookings } = useAllSchedulerBookings(routeMapBookingParams, {
    enabled: dateReady && dispatchView === 'routes' && Boolean(activeRouteTechnicianId),
  });
  const routeMapStops = useMemo(
    () => (routeMapBookings?.data ?? []).filter(isRouteMapBooking),
    [routeMapBookings?.data],
  );
  // Toolbar day-count readout. Counts the RAW day query, so §15 day-bound
  // (unsequenced DAY_FLEXIBLE) visits are included — the backend's range query
  // matches them by scheduledDate even with startAt still null.
  const dayJobCount = isWeek ? filteredWeekBookings.length : (dayBookings?.data.length ?? 0);

  // The business tz the board renders in. Source of truth = the Company
  // Profile tz (tenant.settings.timezone via /settings, NOT-125) — never the
  // first technician's or booking's tz, which can differ (multi-tz / seed) and
  // mislabel the board. The viewer's own tz is used ONLY for the secondary
  // "your time" hint, never to interpret.
  const viewerTz = getViewerTimeZone();
  const showViewerHint = scheduleDisplay.differsFromDisplay(viewerTz);
  const nowIso = new Date().toISOString();

  const setDate = useCallback(
    (next: string) => void navigate({ search: (prev) => ({ ...prev, date: next }) }),
    [navigate],
  );
  const setColorBy = useCallback(
    (next: ColorBy) => {
      try {
        localStorage.setItem('dispatch-colorBy', next);
      } catch {
        /* no-op */
      }
      void navigate({ search: (prev) => ({ ...prev, colorBy: next }) });
    },
    [navigate],
  );
  const setMapMode = useCallback(
    (next: MapMode) => {
      try {
        localStorage.setItem('dispatch-map', next);
      } catch {
        /* no-op */
      }
      void navigate({ search: (prev) => ({ ...prev, map: next }) });
    },
    [navigate],
  );
  const setRoutesTab = useCallback(
    (next: RoutesTab) => {
      void navigate({ search: (prev) => ({ ...prev, routesTab: next }) });
    },
    [navigate],
  );

  // §5.8 — one setter for every faceted filter; an empty value clears the param.
  const setFacet = useCallback(
    (
      key: 'territoryId' | 'jobTypeId' | 'businessUnitId' | 'priority' | 'origin' | 'exceptions',
      value: string | boolean | undefined,
    ) => {
      void navigate({ search: (prev) => ({ ...prev, [key]: value || undefined }) });
    },
    [navigate],
  );
  const clearFacets = useCallback(() => {
    void navigate({
      search: (prev) => ({
        ...prev,
        territoryId: undefined,
        jobTypeId: undefined,
        businessUnitId: undefined,
        priority: undefined,
        origin: undefined,
        exceptions: undefined,
      }),
    });
  }, [navigate]);

  // §5.9 — filter presets, persisted per-tenant in localStorage.
  const activeTenantId = useTenantId();
  const teamStorageKey = tenantScopedKey('dispatch-week-team', activeTenantId);
  // An explicit URL selection wins. Only restore a remembered team that still
  // exists in the active roster; never broaden a missing team to the whole company.
  useEffect(() => {
    if (!isWeek || search.teamId || techsLoading || !teams.length) return;
    let saved: string | null = null;
    try {
      saved = localStorage.getItem(teamStorageKey);
    } catch {
      /* storage can be unavailable */
    }
    const teamId = teams.find((team) => team.id === saved)?.id ?? teams[0]!.id;
    void navigate({ replace: true, search: (prev) => ({ ...prev, teamId }) });
  }, [isWeek, search.teamId, techsLoading, teams, teamStorageKey, navigate]);
  const setTeam = (teamId: string) => {
    setTechFilter('all');
    try {
      localStorage.setItem(teamStorageKey, teamId);
    } catch {
      /* URL selection still works */
    }
    void navigate({ search: (prev) => ({ ...prev, teamId }) });
  };
  const setPeriod = (next: BoardPeriod) => {
    setTechFilter('all');
    void navigate({ search: (prev) => ({ ...prev, period: next }) });
  };
  const openDay = (date: string) => {
    setTechFilter('all');
    void navigate({ search: (prev) => ({ ...prev, period: 'day', date }) });
  };
  const [presets, setPresets] = useState<DispatchFilterPreset[]>(() => loadFilterPresets());
  // Re-hydrate from the active tenant's key when it changes so a tenant switch
  // (without remount) can't show or overwrite another tenant's presets.
  useEffect(() => {
    setPresets(loadFilterPresets());
  }, [activeTenantId]);
  const applyPreset = useCallback(
    (preset: DispatchFilterPreset) => {
      void navigate({
        search: (prev) => ({
          ...prev,
          territoryId: preset.filters.territoryId,
          jobTypeId: preset.filters.jobTypeId,
          businessUnitId: hasBusinessUnits ? preset.filters.businessUnitId : undefined,
          priority: preset.filters.priority,
          // origin (AI-vs-manual) deferred — never re-applied from a preset.
          exceptions: preset.filters.exceptions,
        }),
      });
    },
    [hasBusinessUnits, navigate],
  );
  const saveCurrentPreset = useCallback(() => {
    const name = window.prompt('Name this filter preset')?.trim();
    if (!name) return;
    setPresets(
      saveFilterPreset({
        name,
        filters: {
          territoryId: search.territoryId,
          jobTypeId: search.jobTypeId,
          businessUnitId: effectiveBusinessUnitId,
          priority: search.priority,
          exceptions: search.exceptions,
        },
      }),
    );
  }, [
    search.territoryId,
    search.jobTypeId,
    effectiveBusinessUnitId,
    search.priority,
    search.exceptions,
  ]);
  const removePreset = useCallback((name: string) => {
    setPresets(deleteFilterPreset(name));
  }, []);

  // §5.9 — restore the persisted colorBy / map on mount when the URL didn't set
  // them explicitly (validateSearch hard-defaults, so Dispatch never read its
  // own persisted choice back before). An explicit URL param always wins.
  useEffect(() => {
    const raw = new URLSearchParams(window.location.search);
    const updates: { colorBy?: ColorBy; map?: MapMode } = {};
    if (!raw.has('colorBy')) {
      const saved = localStorage.getItem('dispatch-colorBy');
      if (saved && COLOR_BY_VALUES.includes(saved as ColorBy)) {
        updates.colorBy = saved as ColorBy;
      }
    }
    if (!raw.has('map')) {
      const saved = localStorage.getItem('dispatch-map');
      if (saved === 'on' || saved === 'off') updates.map = saved;
    }
    if (Object.keys(updates).length > 0) {
      void navigate({ search: (prev) => ({ ...prev, ...updates }), replace: true });
    }
    // mount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Selection drives ?bookingId so the timeline block and the map pin stay in
  // sync (the panel emphasizes the pin matching search.bookingId).
  const handleBookingClick = useCallback(
    (booking: SchedulerBooking) => {
      setSelectedBooking(booking);
      setDetailOpen(true);
      void navigate({ search: (prev) => ({ ...prev, bookingId: booking.bookingId }) });
    },
    [navigate],
  );
  const handleSelectRoute = useCallback(
    (technicianId: string) => {
      setSelectedRouteTechnicianId(technicianId);
      void navigate({
        search: (prev) => ({ ...prev, view: 'routes', routesTab: 'map' }),
      });
    },
    [navigate],
  );
  const handleOpenWeatherHold = useCallback(() => {
    if (!canManageWeatherHold) return;
    setWeatherHoldOpen(true);
    setWeatherDrawActive(false);
    void navigate({
      search: (prev) => ({ ...prev, view: 'routes' }),
    });
  }, [canManageWeatherHold, navigate]);
  const handleStartWeatherDraw = useCallback(() => {
    if (!canManageWeatherHold) return;
    setWeatherHoldOpen(true);
    setWeatherDrawActive(true);
    setWeatherDrawRing(null);
    void navigate({
      search: (prev) => ({ ...prev, view: 'routes' }),
    });
  }, [canManageWeatherHold, navigate]);
  const handleWeatherDrawComplete = useCallback(
    (ring: google.maps.LatLngLiteral[]) => {
      if (!canManageWeatherHold) return;
      setWeatherDrawRing(ring);
      setWeatherDrawActive(false);
      setWeatherHoldOpen(true);
    },
    [canManageWeatherHold],
  );
  const handleCloseWeatherHold = useCallback(() => {
    setWeatherHoldOpen(false);
    setWeatherDrawActive(false);
    setWeatherDrawRing(null);
  }, []);

  useEffect(() => {
    if (!canManageWeatherHold && weatherHoldOpen) {
      handleCloseWeatherHold();
    }
  }, [canManageWeatherHold, handleCloseWeatherHold, weatherHoldOpen]);

  useEffect(() => {
    if (dispatchView !== 'routes' || routeSummaries.length === 0) return;
    if (
      selectedRouteTechnicianId &&
      routeSummaries.some((summary) => summary.technicianId === selectedRouteTechnicianId)
    ) {
      return;
    }
    setSelectedRouteTechnicianId(routeSummaries[0]!.technicianId);
  }, [dispatchView, routeSummaries, selectedRouteTechnicianId]);
  const handleDetailOpenChange = useCallback(
    (open: boolean) => {
      setDetailOpen(open);
      if (!open) void navigate({ search: (prev) => ({ ...prev, bookingId: undefined }) });
    },
    [navigate],
  );

  // Deep-link: open the Sheet for ?bookingId once its booking is in scope.
  // Gate on the already-selected booking (not detailOpen) so a user-initiated
  // close doesn't race the async ?bookingId clear and re-open the sheet. Once the
  // param is gone, clear the selection so re-navigating to the same id reopens.
  useEffect(() => {
    if (!search.bookingId) {
      if (selectedBooking) setSelectedBooking(null);
      return;
    }
    if (selectedBooking?.bookingId === search.bookingId) return;
    const match = mapBookings.find((b) => b.bookingId === search.bookingId);
    if (match) {
      setSelectedBooking(match);
      setDetailOpen(true);
      // A deep link to a visit with no time: it is not on the board at all, it
      // is in the queue — so open the queue, or the reader lands on a sheet
      // over a board that shows nothing of what they came for.
      if (unscheduledBookings.some((b) => b.bookingId === match.bookingId)) {
        setQueueOpen(true);
      }
    }
  }, [search.bookingId, selectedBooking, mapBookings, unscheduledBookings]);

  // Gate every date-dependent surface (board, map, day nav)
  // until the effective day is trustworthy — an explicit ?date exists, or the
  // business timezone has resolved. Rendering on the browser-clock fallback would
  // let a cross-tz dispatcher briefly see the wrong board. The effect above pins the real day once
  // settings load, flipping `dateReady`.
  if (!dateReady) {
    return (
      <ScheduleWorkspaceShell hideHeader>
        <div className="flex h-full flex-col items-center justify-center gap-3 bg-background">
          {settingsError ? (
            <>
              <p className="text-sm text-muted-foreground">
                Couldn&apos;t load scheduling settings.
              </p>
              <Button variant="outline" size="sm" onClick={() => void refetchSettings()}>
                Retry
              </Button>
            </>
          ) : (
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          )}
        </div>
      </ScheduleWorkspaceShell>
    );
  }

  return (
    <ScheduleWorkspaceShell hideHeader>
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-background">
        {/* Toolbar: stable date + view controls, with only contextual actions swapping. */}
        <div className="flex shrink-0 flex-wrap items-center gap-3 border-b bg-background px-3 py-2 sm:px-6">
          <div className="flex min-w-[260px] flex-[1_1_360px] flex-wrap items-center gap-3">
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="size-9 shrink-0 lg:size-8"
                aria-label={isWeek ? 'Previous week' : 'Previous day'}
                onClick={() => setDate(toDateString(subDays(dateObj, isWeek ? 7 : 1)))}
              >
                <ChevronLeft className="size-4" />
              </Button>
              <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant={(isWeek ? isThisWeek : isToday) ? 'default' : 'outline'}
                    size="sm"
                    className="h-9 shrink-0 gap-1.5 px-3 text-xs lg:h-8"
                    aria-label={`Choose dispatch date, ${isWeek ? weekLabel : format(dateObj, 'MMMM d, yyyy')}`}
                  >
                    <CalendarDays className="size-4" />
                    {isWeek
                      ? isThisWeek
                        ? 'This week'
                        : weekLabel
                      : isToday
                        ? 'Today'
                        : format(dateObj, 'MMM d, yyyy')}
                    <ChevronDown className="size-3.5" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    required
                    selected={dateObj}
                    defaultMonth={dateObj}
                    today={parseLocalDate(todayInTz(businessTz))}
                    onSelect={(date) => {
                      setDate(toDateString(date));
                      setDatePickerOpen(false);
                    }}
                    initialFocus
                    weekStartsOn={isWeek ? 1 : undefined}
                    modifiers={isWeek ? { week: weekDates.map(parseLocalDate) } : undefined}
                    modifiersClassNames={isWeek ? { week: 'bg-accent' } : undefined}
                  />
                  <div className="border-t p-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full"
                      onClick={() => {
                        setDate(todayInTz(businessTz));
                        setDatePickerOpen(false);
                      }}
                    >
                      {isWeek ? 'Go to this week' : 'Go to today'}
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
              <Button
                variant="outline"
                size="icon"
                className="size-9 shrink-0 lg:size-8"
                aria-label={isWeek ? 'Next week' : 'Next day'}
                onClick={() => setDate(toDateString(addDays(dateObj, isWeek ? 7 : 1)))}
              >
                <ChevronRight className="size-4" />
              </Button>
            </div>
            <div className="flex min-w-0 flex-col">
              <div className="flex min-w-0 items-center gap-2">
                <h1 className="min-w-0 truncate text-base font-semibold">
                  {isWeek ? weekLabel : format(dateObj, 'EEEE, MMMM d, yyyy')}
                </h1>
                {techsLoading ? (
                  <Loader2 className="size-4 animate-spin text-muted-foreground" />
                ) : null}
              </div>
              <p className="text-[11px] text-muted-foreground tabular-nums">
                {dayJobCount} {dayJobCount === 1 ? 'job' : 'jobs'} · {unscheduledBookings.length}{' '}
                unscheduled
              </p>
              {scheduleDisplay.hasDisplayChoice ? (
                <p className="text-[11px] text-muted-foreground">
                  Times in {formatTimeZoneLabel(displayTz)}
                  {showViewerHint ? (
                    <>
                      {' · now '}
                      {formatDateTimeInTimeZone(nowIso, displayTz, {
                        hour: 'numeric',
                        minute: '2-digit',
                      })}{' '}
                      {timeZoneAbbrev(displayTz)} (
                      {formatDateTimeInTimeZone(nowIso, viewerTz, {
                        hour: 'numeric',
                        minute: '2-digit',
                      })}{' '}
                      your time)
                    </>
                  ) : null}
                </p>
              ) : null}
            </div>
          </div>

          <div className="flex min-w-0 flex-[0_1_auto] flex-wrap items-center justify-end gap-2">
            {dispatchView === 'board' ? (
              <>
                {isWeek ? (
                  <DispatchChoiceMenu
                    label="Filter by team"
                    value={selectedTeam?.id ?? ''}
                    onValueChange={setTeam}
                    placeholder="Select a team"
                    className="h-9 w-[170px] lg:h-8"
                    options={teams.map((team) => ({ value: team.id, label: team.name }))}
                  />
                ) : null}
                <DispatchChoiceMenu
                  label="Dispatch view"
                  value={period}
                  onValueChange={(value) => setPeriod(value as BoardPeriod)}
                  className="h-9 w-[90px] lg:h-8"
                  options={[
                    { value: 'day', label: 'Day' },
                    { value: 'week', label: 'Week' },
                  ]}
                />
              </>
            ) : null}
            <ScheduleTimezoneToggle showLabels={false} />
            {dispatchView === 'board' ? (
              <>
                {/* Queue (N) — slides the "Needs a tech" panel in/out. */}
                <Button
                  variant={queueOpen || queueOnly ? 'secondary' : 'outline'}
                  size="sm"
                  className="h-9 shrink-0 gap-1.5 px-3 text-xs lg:h-8"
                  onClick={() => {
                    if (queueOnly)
                      void navigate({ search: (prev) => ({ ...prev, queue: undefined }) });
                    else setQueueOpen((v) => !v);
                  }}
                >
                  <ClipboardList className="size-3.5" />
                  Unscheduled
                  {unscheduledBookings.length > 0 ? (
                    <Badge
                      variant="outline"
                      className="ml-0.5 border-warning/40 bg-warning/10 px-1.5 text-warning"
                    >
                      {unscheduledBookings.length}
                    </Badge>
                  ) : null}
                </Button>
                {/* Map — toggles the companion panel. */}
                {!isWeek && (
                  <Button
                    variant={mapMode === 'on' ? 'secondary' : 'outline'}
                    size="sm"
                    className="h-9 shrink-0 gap-1.5 px-3 text-xs lg:h-8"
                    onClick={() => setMapMode(mapMode === 'on' ? 'off' : 'on')}
                  >
                    <MapIcon className="size-3.5" />
                    Map
                  </Button>
                )}
                {/* Live — the §15 in-flight cockpit. */}
                <Button
                  asChild
                  variant="outline"
                  size="sm"
                  className="h-9 shrink-0 gap-1.5 px-3 text-xs lg:h-8"
                >
                  <Link to="/dashboard/dispatch/live">
                    <RadioTower className="size-3.5 text-emerald-500" />
                    Live
                  </Link>
                </Button>
                {/* Filters — tech filter + color-by, tucked off the bar. */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-9 shrink-0 gap-1.5 px-3 text-xs lg:h-8"
                    >
                      <SlidersHorizontal className="size-3.5" />
                      Filters
                      {activeFilterCount > 0 ? (
                        <Badge variant="secondary" className="ml-0.5 px-1.5">
                          {activeFilterCount}
                        </Badge>
                      ) : null}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="end"
                    className="max-h-[80vh] w-64 space-y-4 overflow-y-auto"
                  >
                    {facetFilterCount > 0 ? (
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold">Filters</p>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-[11px] text-muted-foreground"
                          onClick={clearFacets}
                        >
                          Clear all
                        </Button>
                      </div>
                    ) : null}
                    {/* §5.9 — saved filter presets (per-device). */}
                    {presets.length > 0 || facetFilterCount > 0 ? (
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-medium text-muted-foreground">Presets</p>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-[11px]"
                            disabled={facetFilterCount === 0}
                            onClick={saveCurrentPreset}
                          >
                            Save current
                          </Button>
                        </div>
                        {presets.length > 0 ? (
                          <div className="flex flex-wrap gap-1.5">
                            {presets.map((p) => (
                              <span
                                key={p.name}
                                className="inline-flex items-center gap-1 rounded-full border bg-muted/40 py-0.5 pl-2 pr-1 text-[11px]"
                              >
                                <button
                                  type="button"
                                  className="max-w-[120px] truncate hover:underline"
                                  onClick={() => applyPreset(p)}
                                >
                                  {p.name}
                                </button>
                                <button
                                  type="button"
                                  className="text-muted-foreground hover:text-foreground"
                                  onClick={() => removePreset(p.name)}
                                  aria-label={`Delete preset ${p.name}`}
                                >
                                  <X className="size-3" />
                                </button>
                              </span>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                    <div className="space-y-1.5">
                      <p className="text-xs font-medium text-muted-foreground">Technician</p>
                      <DispatchChoiceMenu
                        label="Technician"
                        value={techFilter}
                        onValueChange={setTechFilter}
                        options={[
                          { value: 'all', label: 'All technicians' },
                          ...filterTechnicians.map((t) => ({ value: t.id, label: t.name })),
                        ]}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <p className="text-xs font-medium text-muted-foreground">Territory</p>
                      <DispatchChoiceMenu
                        label="Territory"
                        value={search.territoryId ?? 'all'}
                        onValueChange={(v) => setFacet('territoryId', v === 'all' ? undefined : v)}
                        options={[
                          { value: 'all', label: 'All territories' },
                          ...territories.map((t) => ({ value: t.id, label: t.name })),
                        ]}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <p className="text-xs font-medium text-muted-foreground">Job type</p>
                      <DispatchChoiceMenu
                        label="Job type"
                        value={search.jobTypeId ?? 'all'}
                        onValueChange={(v) => setFacet('jobTypeId', v === 'all' ? undefined : v)}
                        options={[
                          { value: 'all', label: 'All job types' },
                          ...jobTypes.map((t) => ({ value: t.id, label: t.name })),
                        ]}
                      />
                    </div>
                    {/* NOT-542 — Business Unit facet. Zero-cost: only shown when the
                    tenant has ≥1 active BU, so it never forces the concept on tenants
                    that don't use it. "Unassigned" maps to the backend sentinel. */}
                    {hasBusinessUnits ? (
                      <div className="space-y-1.5">
                        <p className="text-xs font-medium text-muted-foreground">Business unit</p>
                        <DispatchChoiceMenu
                          label="Business unit"
                          value={effectiveBusinessUnitId ?? 'all'}
                          onValueChange={(v) =>
                            setFacet('businessUnitId', v === 'all' ? undefined : v)
                          }
                          options={[
                            { value: 'all', label: 'All business units' },
                            { value: UNASSIGNED_BUSINESS_UNIT, label: 'Unassigned BU' },
                            ...businessUnits.map((unit) => ({
                              value: unit.id,
                              label: (
                                <span className="flex min-w-0 items-center gap-2">
                                  <span
                                    className="h-2.5 w-2.5 shrink-0 rounded-full border"
                                    style={{ backgroundColor: unit.color }}
                                  />
                                  <span className="truncate">{unit.name}</span>
                                </span>
                              ),
                            })),
                          ]}
                        />
                      </div>
                    ) : null}
                    <div className="space-y-1.5">
                      <p className="text-xs font-medium text-muted-foreground">Priority</p>
                      <DispatchChoiceMenu
                        label="Priority"
                        value={search.priority ?? 'all'}
                        onValueChange={(v) => setFacet('priority', v === 'all' ? undefined : v)}
                        options={[
                          { value: 'all', label: 'Any priority' },
                          ...PRIORITY_VALUES.map((p) => ({
                            value: p,
                            label: p.charAt(0) + p.slice(1).toLowerCase(),
                          })),
                        ]}
                      />
                    </div>
                    {/* §5.8/§21.2 — AI-vs-manual "Source" facet hidden until AI
                    auto-dispatch lands (the signal doesn't exist yet). */}
                    {/* "Override conflicts only" facet removed pre-launch — the
                    team doesn't want any override surface exposed for now. The
                    search-param plumbing (search.exceptions / hasExceptions) is
                    left dead so a saved preset/URL can't error; no UI sets it. */}
                    <div className="space-y-1.5">
                      <p className="text-xs font-medium text-muted-foreground">Color by</p>
                      <Tabs value={colorBy} onValueChange={(v) => setColorBy(v as ColorBy)}>
                        {/* NOT-329 — `confirmation` stays a valid mode (URL/persisted),
                        but its tab is hidden until the customer-confirmation field
                        is plumbed; with no data it would read identically to Status
                        (a dead control). Re-add the trigger when the field lands. */}
                        <TabsList className="grid h-9 w-full grid-cols-4">
                          <TabsTrigger value="jobtype" className="text-xs">
                            Type
                          </TabsTrigger>
                          <TabsTrigger value="status" className="text-xs">
                            Status
                          </TabsTrigger>
                          <TabsTrigger value="priority" className="text-xs">
                            Priority
                          </TabsTrigger>
                          <TabsTrigger value="tech" className="text-xs">
                            Tech
                          </TabsTrigger>
                        </TabsList>
                      </Tabs>
                      {/* The legend belongs with the control that sets it: the
                      colours are meaningless until someone says what they map
                      to, and a dispatcher asking "what is this colour" is
                      already looking at this panel. Scoped to the visits on the
                      board so the two unbounded modes (job type, tech) stay
                      readable. */}
                      <DispatchColorLegend bookings={legendBookings} colorBy={colorBy} />
                    </div>
                  </PopoverContent>
                </Popover>
                {/* Teams — the board's grouping is configured here, so it lives
                    on the board rather than in the sidebar. */}
                {canManageTeams ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 shrink-0 gap-1.5 px-3 text-xs lg:h-8"
                    onClick={() => setTeamsOpen(true)}
                  >
                    <Rows3 className="size-3.5" />
                    Teams
                  </Button>
                ) : null}
              </>
            ) : (
              <>
                <DispatchChoiceMenu
                  label="Territory"
                  value={search.territoryId ?? 'all'}
                  onValueChange={(v) => setFacet('territoryId', v === 'all' ? undefined : v)}
                  placeholder="All territories"
                  className="h-8 w-[190px] max-w-full"
                  options={[
                    { value: 'all', label: 'All territories' },
                    ...territories.map((t) => ({ value: t.id, label: t.name })),
                  ]}
                />
                {canManageWeatherHold ? (
                  <Button
                    variant={weatherHoldOpen ? 'secondary' : 'outline'}
                    size="sm"
                    className="h-8 gap-1.5 px-3 text-xs"
                    onClick={handleOpenWeatherHold}
                  >
                    <CloudRain className="size-3.5" />
                    Weather hold
                  </Button>
                ) : null}
                <Tabs value={routesTab} onValueChange={(v) => setRoutesTab(v as RoutesTab)}>
                  <TabsList className="h-8">
                    <TabsTrigger value="overview" className="px-3 text-xs">
                      Overview
                    </TabsTrigger>
                    <TabsTrigger value="map" className="px-3 text-xs">
                      Map
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </>
            )}
          </div>
        </div>

        {/* Cockpit: calm schedule by default; Queue + Map slide in on demand.
            Both are inline (push) panels, never modal overlays — the queue cards
            are drag sources and the map is read alongside the timeline. */}
        {dispatchView === 'board' ? (
          <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
            {(queueOpen || queueOnly) && (
              <div
                className={cn(
                  'min-h-0',
                  queueOnly
                    ? 'min-w-0 flex-1'
                    : 'order-2 h-[38vh] max-h-[400px] shrink-0 border-t lg:order-none lg:h-auto lg:max-h-none lg:w-[320px] lg:border-r lg:border-t-0 xl:w-[340px]',
                )}
              >
                <DispatchStageQueues
                  view={queueView}
                  onViewChange={setQueueView}
                  colorBy={colorBy}
                  bookings={unscheduledBookings}
                  isLoading={queueQuery.isLoading}
                  isError={queueQuery.isError}
                  onRetry={() => void queueQuery.refetch()}
                  pipelineId={search.pipelineId}
                  onPipelineChange={(pipelineId) =>
                    void navigate({
                      search: (prev) => ({
                        ...prev,
                        pipelineId: pipelineId === 'all' ? undefined : pipelineId,
                      }),
                    })
                  }
                  onSelectBooking={handleBookingClick}
                  expanded={queueOnly}
                  onClose={() => {
                    setQueueOpen(false);
                    if (queueOnly)
                      void navigate({ search: (prev) => ({ ...prev, queue: undefined }) });
                  }}
                />
              </div>
            )}
            {!queueOnly && (
              <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                {isWeek ? (
                  techniciansError || weekError ? (
                    <div
                      className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-sm"
                      role="alert"
                    >
                      <p>Couldn&apos;t load the weekly schedule.</p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          void refetchWeek();
                          void refetchTechnicians();
                        }}
                      >
                        Retry
                      </Button>
                    </div>
                  ) : weekLoading || (techsLoading && technicians.length === 0) ? (
                    <div
                      className="flex flex-1 items-center justify-center p-6"
                      role="status"
                      aria-label="Loading weekly schedule"
                    >
                      <Loader2 className="size-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : !selectedTeam || weekTechnicianIds.length === 0 ? (
                    <div className="flex flex-1 items-center justify-center p-6 text-sm text-muted-foreground">
                      {teams.length === 0
                        ? 'No active technicians available.'
                        : !selectedTeam
                          ? 'Select a team to view its weekly schedule.'
                          : 'No technicians match the selected filters.'}
                    </div>
                  ) : (
                    <DispatchWeekBoard
                      date={currentDate}
                      timezone={businessTz!}
                      bookings={filteredWeekBookings}
                      technicianIds={weekTechnicianIds}
                      colorBy={colorBy}
                      selectedBookingId={detailOpen ? (selectedBooking?.bookingId ?? null) : null}
                      layoutRev={`${queueOpen}|${search.teamId}`}
                      onSelectBooking={handleBookingClick}
                      onOpenDay={openDay}
                    />
                  )
                ) : (
                  <DispatchBoard
                    selectedBookingId={detailOpen ? (selectedBooking?.bookingId ?? null) : null}
                    date={currentDate}
                    colorBy={colorBy}
                    technicianFilter={techFilter !== 'all' ? [techFilter] : undefined}
                    filters={boardFilters}
                    layoutRev={`${queueOpen}|${mapMode}`}
                    onSelectBooking={handleBookingClick}
                    hoveredBookingId={hoveredBookingId}
                    onHoverBooking={setHoveredBookingId}
                  />
                )}
              </div>
            )}
            {!queueOnly && !isWeek && mapMode === 'on' ? (
              <aside className="relative hidden w-[360px] shrink-0 border-l lg:block">
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1.5 z-10 size-6 text-muted-foreground"
                  onClick={() => setMapMode('off')}
                  aria-label="Close map"
                >
                  <X className="size-3.5" />
                </Button>
                <DispatchMapPanel
                  bookings={mapPanelBookings}
                  // The map always colors pins by CREW — on a map "whose stop is
                  // this?" reads at a glance, and each crew's zone is legible. The
                  // board's color-by (jobtype default, NOT-432) styles the TILES,
                  // not the map, so this is decoupled on purpose.
                  colorBy="tech"
                  selectedBookingId={search.bookingId}
                  onSelectBooking={handleBookingClick}
                  routeTechnicianId={techFilter !== 'all' ? techFilter : undefined}
                  routeDate={currentDate}
                  externalHoveredId={hoveredBookingId}
                  onHoverBooking={setHoveredBookingId}
                />
              </aside>
            ) : null}
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col">
            {weatherHoldOpen && canManageWeatherHold ? (
              <WeatherHoldFlowPanel
                date={currentDate}
                bookings={routeMapStops}
                territories={territories}
                colorBy={colorBy}
                selectedBookingId={search.bookingId}
                initialTerritoryId={search.territoryId}
                drawnRing={weatherDrawRing}
                drawActive={weatherDrawActive}
                timezone={displayTz}
                onStartDraw={handleStartWeatherDraw}
                onCancelDraw={() => setWeatherDrawActive(false)}
                onClearDraw={() => setWeatherDrawRing(null)}
                onDrawAreaComplete={handleWeatherDrawComplete}
                onSelectBooking={handleBookingClick}
                onClose={handleCloseWeatherHold}
              />
            ) : routesTab === 'overview' ? (
              <RoutesOverview
                summaries={routeSummaries}
                bookings={routeMapStops}
                timezone={displayTz}
                isLoading={routeSummariesLoading}
                onSelectRoute={handleSelectRoute}
              />
            ) : (
              <RoutesMapView
                summaries={routeSummaries}
                bookings={routeMapStops}
                colorBy={colorBy}
                timezone={displayTz}
                selectedTechnicianId={activeRouteTechnicianId}
                selectedBookingId={search.bookingId}
                routeDate={currentDate}
                isToday={isToday}
                onSelectRoute={setSelectedRouteTechnicianId}
                onSelectBooking={handleBookingClick}
                weatherDrawActive={canManageWeatherHold ? weatherDrawActive : false}
                onWeatherDrawComplete={canManageWeatherHold ? handleWeatherDrawComplete : undefined}
              />
            )}
          </div>
        )}
      </div>

      <BookingDetailSheet
        booking={selectedBooking}
        open={detailOpen}
        onOpenChange={handleDetailOpenChange}
      />

      <Sheet open={teamsOpen} onOpenChange={setTeamsOpen}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-xl">
          <TeamsManagementPanel />
        </SheetContent>
      </Sheet>
    </ScheduleWorkspaceShell>
  );
}
